import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatNumber, formatToman, formatTomanInt } from "../utils/format";
import type { DashboardUsageData } from "./StackedAreaChart";

interface RadialBarChartProps {
  data?: DashboardUsageData;
  metric: string;
  duration?: string;
  loading?: boolean;
}

function useAnimatedFrac(target: number): number {
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(0);
  const prevRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    if (target === prevRef.current && Math.abs(displayRef.current - target) < 0.001) return;
    const start = displayRef.current;
    const end = target;
    if (Math.abs(start - end) < 0.0005) {
      setDisplay(end);
      prevRef.current = end;
      return;
    }
    const duration = 620;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = start + (end - start) * eased;
      setDisplay(v);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else {
        prevRef.current = end;
        displayRef.current = end;
        setDisplay(end);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  // Initial mount: animate from 0
  useEffect(() => {
    if (prevRef.current !== 0) return;
    // trigger initial animation if target > 0 and we started at 0
    if (target > 0) {
      // effect above will run on target change (already)
    }
  }, [target]);

  return display;
}

const OTHERS = "others";
const OTHERS_LABEL = "سایر";
const PALETTE = ["#22C55E", "#60A5FA", "#A78BFA", "#FB923C"];
const OTHERS_COLOR = "#94A3B8";

const SIZE = 240;
const CX = SIZE / 2;
const CY = SIZE / 2 + 6;
const BAR_WIDTH = 14;
const START_ANGLE = -90; // 12 o'clock (top); all bars start here
const MAX_SWEEP = 270; // clockwise extent

interface ArcPoint {
  x: number;
  y: number;
}

function polar(cx: number, cy: number, r: number, angleDeg: number): ArcPoint {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const start = polar(cx, cy, r, a0);
  const end = polar(cx, cy, r, a1);
  const largeArc = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function formatValue(metric: string, value: number): string {
  if (metric === "spend") return formatToman(value);
  return formatNumber(value);
}

function formatCenterValue(metric: string, value: number): string {
  if (metric === "spend") return formatTomanInt(value);
  return formatNumber(value);
}

function metricUnit(metric: string): string {
  if (metric === "spend") return "تومان";
  if (metric === "requests") return "درخواست";
  return "توکن";
}

function metricLabel(metric: string): string {
  if (metric === "spend") return "هزینه";
  if (metric === "requests") return "درخواست‌ها";
  return "توکن‌ها";
}

function RadialBar({
  frac,
  color,
  r,
  dimmed,
  isHovered,
  label,
  value,
  delay,
  onEnter,
  onLeave,
}: {
  frac: number;
  color: string;
  r: number;
  dimmed: boolean;
  isHovered: boolean;
  label: string;
  value: number;
  delay: number;
  onEnter: () => void;
  onLeave: () => void;
}) {
  const animatedFrac = useAnimatedFrac(frac);
  const valueEnd = START_ANGLE + MAX_SWEEP * animatedFrac;
  return (
    <g
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{
        cursor: "pointer",
        transition: `opacity 420ms ease ${delay}ms`,
        opacity: dimmed ? 0.3 : 1,
      }}
    >
      {/* Track */}
      <path
        d={arcPath(CX, CY, r, START_ANGLE, START_ANGLE + MAX_SWEEP)}
        stroke="rgb(var(--color-track))"
        strokeWidth={BAR_WIDTH}
        strokeLinecap="round"
        fill="none"
        style={{ transition: `opacity 400ms ease ${delay}ms` }}
      />
      {/* Value bar - animated via frac interpolation */}
      {animatedFrac > 0.001 && (
        <path
          d={arcPath(CX, CY, r, START_ANGLE, valueEnd)}
          stroke={color}
          strokeWidth={BAR_WIDTH}
          strokeLinecap="round"
          fill="none"
          style={{
            filter: isHovered ? `drop-shadow(0 2px 6px ${color}66)` : undefined,
            transition: `filter 200ms ease, opacity 420ms ease ${delay}ms`,
            opacity: 1,
          }}
        />
      )}
    </g>
  );
}

export default function RadialBarChart({ data, metric, duration, loading }: RadialBarChartProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const flowKey = `${metric}-${duration ?? ""}`;
  const flowKeyRef = useRef(flowKey);
  const pendingRef = useRef<string | null>(null);
  const prevDataRef = useRef(data);

  const items = useMemo(() => {
    const models = data?.models || [];
    const totals = data?.totals || {};
    const colorFor = new Map<string, string>();
    models.forEach((m, i) => {
      if (m === OTHERS) colorFor.set(m, OTHERS_COLOR);
      else colorFor.set(m, PALETTE[i % PALETTE.length]);
    });
    return {
      list: models
        .map((m) => ({ name: m, value: Number(totals[m]) || 0, color: colorFor.get(m) || "#999" }))
        .filter((it) => it.value > 0)
        .sort((a, b) => b.value - a.value),
      colors: colorFor,
    };
  }, [data]);

  const maxValue = Math.max(1, ...items.list.map((it) => it.value));
  const total = items.list.reduce((sum, it) => sum + it.value, 0);

  // Flow animation: keep switching until new data for the new key arrives
  useLayoutEffect(() => {
    if (flowKey === flowKeyRef.current) return;
    flowKeyRef.current = flowKey;
    pendingRef.current = flowKey;
    setIsSwitching(true);
  }, [flowKey]);

  useEffect(() => {
    if (!pendingRef.current) {
      prevDataRef.current = data;
      return;
    }
    if (data === prevDataRef.current) return;
    if (loading) return;
    const hasReal = !!(data && (data as DashboardUsageData).buckets?.length);
    if (!hasReal) return;
    let raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        setIsSwitching(false);
        pendingRef.current = null;
        prevDataRef.current = data;
        flowKeyRef.current = flowKey;
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [data, flowKey, loading]);

  if (loading) {
    return (
      <div style={{ height: SIZE }} className="flex items-center justify-center">
        <div className="h-44 w-full bg-surface-hover rounded animate-pulse" />
      </div>
    );
  }

  if (!items.list.length) {
    return (
      <div style={{ height: SIZE }} className="flex items-center justify-center text-text-tertiary">
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const radiusFor = (i: number) => 34 + i * (BAR_WIDTH + 2.5);
  const chartSwitchClass = `chart-transition ${isSwitching ? "chart-switching" : "chart-idle"}`;

  return (
    <div className="flex flex-col items-center" dir="ltr">
      <div className={`relative w-full max-w-[280px] ${chartSwitchClass}`}>
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full block">
          {items.list.map((it, i) => {
            const r = radiusFor(i);
            const frac = it.value / maxValue;
            const dimmed = hovered !== null && hovered !== it.name;
            const isHovered = hovered === it.name;
            return (
              <RadialBar
                key={it.name}
                frac={frac}
                color={it.color}
                r={r}
                dimmed={dimmed}
                isHovered={isHovered}
                label={it.name}
                value={it.value}
                delay={i * 70}
                onEnter={() => setHovered(it.name)}
                onLeave={() => setHovered(null)}
              />
            );
          })}

          {/* Center total with halo for contrast against arcs */}
          <defs>
            <filter id="radial-text-halo" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.18" floodColor="#000" />
            </filter>
          </defs>
          <text
            x={CX}
            y={CY - 4}
            textAnchor="middle"
            className="font-number"
            style={{
              fontSize: 26,
              fontWeight: 600,
              fill: "rgb(var(--text-main))",
              paintOrder: "stroke",
              stroke: "rgb(var(--bg-surface))",
              strokeWidth: 6,
              strokeLinejoin: "round",
              strokeLinecap: "round",
              filter: "url(#radial-text-halo)",
            }}
          >
            {formatCenterValue(metric, total)}
          </text>
          <text
            x={CX}
            y={CY + 16}
            textAnchor="middle"
            style={{
              fontSize: 11,
              fill: "rgb(var(--text-tertiary))",
              paintOrder: "stroke",
              stroke: "rgb(var(--bg-surface))",
              strokeWidth: 4,
              strokeLinejoin: "round",
              strokeLinecap: "round",
            }}
          >
            {metricLabel(metric)}
          </text>
        </svg>

        {/* Hover tooltip */}
        {hovered && (
          <div
            dir="rtl"
            className="absolute z-30 left-1/2 -translate-x-1/2 top-0 px-3 py-2 rounded-btn whitespace-nowrap pointer-events-none"
            style={{
              background: "rgba(26,26,26,0.92)",
              color: "#fff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {(() => {
              const it = items.list.find((x) => x.name === hovered);
              if (!it) return null;
              const pct = total > 0 ? (it.value / total) * 100 : 0;
              return (
                <>
                  <div className="flex items-center justify-between gap-5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: it.color }}
                      />
                      <span dir="ltr" className="text-[11px] truncate max-w-[150px]">
                        {it.name === OTHERS ? OTHERS_LABEL : it.name}
                      </span>
                    </div>
                    <span className="text-xs font-medium" style={{ color: "rgb(var(--chart-green))" }}>
                      {formatValue(metric, it.value)} {metricUnit(metric)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-5">
                    <span className="text-[11px]" style={{ opacity: 0.8 }}>سهم</span>
                    <span className="text-xs font-medium">{pct.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪</span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Legend - staggered flow */}
      <div className={`w-full mt-4 space-y-1.5 chart-transition ${isSwitching ? "chart-switching" : "chart-idle"}`} style={{ transitionDelay: isSwitching ? "90ms" : "0ms" }}>
        {items.list.map((it, idx) => {
          const pct = total > 0 ? (it.value / total) * 100 : 0;
          return (
            <div
              key={it.name}
              className={`flex items-center justify-between gap-3 px-1 py-1 rounded-btn transition-colors ${
                hovered === it.name ? "bg-surface-hover" : ""
              }`}
              onMouseEnter={() => setHovered(it.name)}
              onMouseLeave={() => setHovered(null)}
              style={{
                opacity: isSwitching ? 0 : 1,
                transform: isSwitching ? "translateY(4px)" : "translateY(0)",
                transition: `opacity 360ms ease ${idx * 45}ms, transform 400ms cubic-bezier(0.22,1,0.36,1) ${idx * 45}ms, background-color 150ms`,
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: it.color }} />
                <span dir="ltr" className="text-body text-text-main truncate">
                  {it.name === OTHERS ? OTHERS_LABEL : it.name}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-body font-number text-text-main">{formatValue(metric, it.value)}</span>
                <span className="text-[10px] text-text-tertiary w-10 text-left">
                  {pct.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
